import { z } from "zod";
import { Actor, Surface } from "./actor.js";
import { BlastRadius, PolicyOutcome } from "./radius.js";
import { JOB_REF_PATTERN } from "./job-ref.js";
/** The literal artefact — question 1 of six. Never JSON as the primary view. */
export const Artefact = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("text"), body: z.string() }),
    z.object({ kind: z.literal("markdown"), body: z.string() }),
    z.object({ kind: z.literal("html"), body: z.string() }),
    z.object({
        kind: z.literal("email"),
        to: z.array(z.string()),
        subject: z.string(),
        body_html: z.string(),
    }),
    z.object({
        kind: z.literal("record"),
        table: z.string(),
        /** field → value as it will be written, already normalised */
        fields: z.record(z.string(), z.unknown()),
    }),
]);
export const Undo = z.enum(["reversible", "with_effort", "irreversible"]);
/** The six questions. No request may render without answering all six
 *  (EMMA-GOVERNANCE-CONSOLIDATED.md §3). A command without a renderer cannot be proposed. */
export const SixQuestions = z.object({
    /** 1. What exactly will happen */
    what: Artefact,
    /** 2. Why now — the trigger, with a link back to the run */
    why_now: z.object({ trigger: z.string(), run_id: z.string().uuid().nullable() }),
    /** 3. What she considered */
    considered: z.object({
        reasoning: z.string(),
        rejected: z.array(z.string()),
        confidence: z.number().min(0).max(1),
    }),
    /** 4. Who it touches, what it costs */
    touches: z.object({
        recipients: z.array(z.string()),
        amount_aud: z.number().nullable(),
        tables: z.array(z.string()),
    }),
    /** 5. If you say no */
    if_no: z.string(),
    /** 6. Can it be undone */
    undo: z.object({ kind: Undo, path: z.string().nullable() }),
});
/** Where the question is delivered: inline where the actor is (ADR-003, closed
 *  decision "the question goes to the channel the human is in"). */
export const Delivery = z.object({
    surface: Surface,
    channel: z.enum(["inline", "email", "whatsapp", "wechat", "sms"]).default("inline"),
    /** who may decide; fail-closed audience rule ported from emma-hub audience.ts */
    audience: z.array(z.string()).min(1),
});
export const ActionStatus = z.enum(["executed", "failed", "denied", "pending", "rejected", "expired"]);
export const CaseStatus = z.enum(["pending", "decided", "expired"]);
/** bb.case — a rendered question awaiting a decision. */
export const Case = z.object({
    id: z.string().uuid(),
    action_id: z.string().uuid(),
    command: z.string(),
    radius: BlastRadius,
    proposed_by: Actor,
    questions: SixQuestions,
    deliver_to: Delivery,
    status: CaseStatus,
    decision: z.enum(["approve", "reject", "edit", "request_revision"]).nullable().optional(),
    action_status: ActionStatus.optional(),
    delivery_status: z.enum(["queued", "claimed", "prepared", "drafted", "submitting", "submitted", "confirmed", "uncertain", "failed", "cancelled"]).optional(),
    delivery_error: z.string().nullable().optional(),
    delivery_id: z.string().uuid().optional(),
    /** Exact saved action/PO target, never inferred from the rendered text. */
    job_ref: z.string().regex(JOB_REF_PATTERN).nullable().optional(),
    /** The short request number Jacky reads on the card (4 Nov 2026). A send approval carries the number of the draft it
     *  sends, so the draft card and its send card show the same #. Null before the column existed. */
    request_no: z.number().int().positive().nullable().optional(),
    /** Exact communication draft behind a send approval. */
    communication_id: z.string().uuid().nullable().optional(),
    /** Exact purchase order behind a send_po approval, so the card can show its PDF before the click (13 Oct 2026). */
    po_id: z.string().uuid().nullable().optional(),
    /** The supplier behind a create_supplier_bills approval, so its Statements tab shows the card (4 Nov 2026). */
    supplier_id: z.string().max(80).nullable().optional(),
    revision_of: z.string().uuid().nullable().optional(),
    replacement_case_id: z.string().uuid().nullable().optional(),
    revision_request: z.object({ reason: z.string(), requested_by: Actor, requested_at: z.string().datetime() }).nullable().optional(),
    created_at: z.string().datetime(),
    expires_at: z.string().datetime().nullable(),
});
/** What a consumer posts back: `POST /decide`. Consumers never execute. */
export const Verdict = z.object({
    case_id: z.string().uuid(),
    decided_by: Actor,
    decision: z.enum(["approve", "reject", "edit", "request_revision"]),
    /** for `edit`: the args as corrected by the human; re-rendered before execute */
    edited_args: z.record(z.string(), z.unknown()).nullable().default(null),
    reason: z.string().nullable().default(null),
});
/** bb.action — the audit row on EVERY path, auto included, with the undo path. */
export const Action = z.object({
    id: z.string().uuid(),
    command: z.string(),
    radius: BlastRadius,
    actor: Actor,
    surface: Surface,
    policy: PolicyOutcome,
    status: ActionStatus,
    case_id: z.string().uuid().nullable(),
    idempotency_key: z.string(),
    args: z.record(z.string(), z.unknown()),
    result: z.unknown().nullable(),
    undo: z.object({ kind: Undo, path: z.string().nullable() }),
    run_id: z.string().uuid().nullable(),
    test_set_id: z.string().uuid().nullable(),
    at: z.string().datetime(),
});
//# sourceMappingURL=case.js.map